// =====================================================================
// My Podcasts — single stable module.
//
// IMPORTANT: This file intentionally exists as ONE physical file at
// src/lib/podcasts.ts. Do NOT split it into a folder/barrel
// (`src/lib/podcasts/index.ts`) — that move caused recurring Vite
// dev/HMR hard-refresh flutter on every page after 2026-05-23. The
// flutter went away only after restoring the original single-file
// shape. If this file gets long, prefer co-located helpers (e.g.
// `src/lib/podcastsServer.ts`) over replacing it with a folder.
// =====================================================================

import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import type { LibraryMode } from "@/lib/tier";
import type { Workspace } from "@/hooks/useWorkspace";

// ---------------------------------------------------------------------
// Types + constants
// ---------------------------------------------------------------------

export type PodcastSubscription = Tables<"podcast_subscriptions">;
export type PodcastEpisode = Tables<"podcast_episodes">;
export type PodcastTranscript = Tables<"podcast_episode_transcripts">;
export type PodcastCategory = Tables<"podcast_categories_cache">;

export type EpisodeFilter = "starred" | "archive" | "all";

export const scopeFilter = (
  mode: LibraryMode,
  active: Workspace | null,
  workspaces: Workspace[],
): string[] => {
  if (!active) return [];
  if (mode === "shared" && workspaces.length > 1) return workspaces.map((w) => w.id);
  return [active.id];
};

// ---- ISO 639-1 language list (curated top, then alphabetical) ----------
// Used by the Discover toolbar Language <Select>.
export const TOP_LANGUAGES: Array<{ code: string; label: string }> = [
  { code: "en", label: "English" },
  { code: "es", label: "Spanish" },
  { code: "fr", label: "French" },
  { code: "de", label: "German" },
  { code: "pt", label: "Portuguese" },
  { code: "it", label: "Italian" },
  { code: "ja", label: "Japanese" },
  { code: "zh", label: "Chinese (Mandarin)" },
  { code: "hi", label: "Hindi" },
  { code: "ar", label: "Arabic" },
];

export const ALL_LANGUAGES: Array<{ code: string; label: string }> = [
  { code: "af", label: "Afrikaans" }, { code: "sq", label: "Albanian" },
  { code: "ar", label: "Arabic" }, { code: "hy", label: "Armenian" },
  { code: "az", label: "Azerbaijani" }, { code: "eu", label: "Basque" },
  { code: "be", label: "Belarusian" }, { code: "bn", label: "Bengali" },
  { code: "bs", label: "Bosnian" }, { code: "bg", label: "Bulgarian" },
  { code: "ca", label: "Catalan" }, { code: "zh", label: "Chinese" },
  { code: "hr", label: "Croatian" }, { code: "cs", label: "Czech" },
  { code: "da", label: "Danish" }, { code: "nl", label: "Dutch" },
  { code: "en", label: "English" }, { code: "et", label: "Estonian" },
  { code: "fi", label: "Finnish" }, { code: "fr", label: "French" },
  { code: "gl", label: "Galician" }, { code: "ka", label: "Georgian" },
  { code: "de", label: "German" }, { code: "el", label: "Greek" },
  { code: "he", label: "Hebrew" }, { code: "hi", label: "Hindi" },
  { code: "hu", label: "Hungarian" }, { code: "is", label: "Icelandic" },
  { code: "id", label: "Indonesian" }, { code: "ga", label: "Irish" },
  { code: "it", label: "Italian" }, { code: "ja", label: "Japanese" },
  { code: "kk", label: "Kazakh" }, { code: "ko", label: "Korean" },
  { code: "lv", label: "Latvian" }, { code: "lt", label: "Lithuanian" },
  { code: "mk", label: "Macedonian" }, { code: "ms", label: "Malay" },
  { code: "mt", label: "Maltese" }, { code: "no", label: "Norwegian" },
  { code: "fa", label: "Persian" }, { code: "pl", label: "Polish" },
  { code: "pt", label: "Portuguese" }, { code: "ro", label: "Romanian" },
  { code: "ru", label: "Russian" }, { code: "sr", label: "Serbian" },
  { code: "sk", label: "Slovak" }, { code: "sl", label: "Slovenian" },
  { code: "es", label: "Spanish" }, { code: "sw", label: "Swahili" },
  { code: "sv", label: "Swedish" }, { code: "ta", label: "Tamil" },
  { code: "te", label: "Telugu" }, { code: "th", label: "Thai" },
  { code: "tr", label: "Turkish" }, { code: "uk", label: "Ukrainian" },
  { code: "ur", label: "Urdu" }, { code: "vi", label: "Vietnamese" },
  { code: "cy", label: "Welsh" },
];

export const languageLabel = (code: string | null | undefined): string => {
  if (!code) return "—";
  const c = code.toLowerCase().slice(0, 2);
  return ALL_LANGUAGES.find((l) => l.code === c)?.label ?? code.toUpperCase();
};

// ---- Transcription credit math (mirrored server-side) ------------------
// credits = ceil(minutes / 2), minimum 1. Matches Deepgram Nova-3 cost
// at $0.0043/min with ~77.7% margin on smallest top-up bundle.
export const transcriptionCreditCost = (durationSeconds: number | null | undefined): number => {
  const s = Math.max(0, Math.round(durationSeconds ?? 0));
  if (s === 0) return 1;
  return Math.max(1, Math.ceil(s / 60 / 2));
};

// ---------------------------------------------------------------------
// Discover / search (PodcastIndex via edge functions)
// ---------------------------------------------------------------------

export type PodcastSearchResult = {
  feed_id: number;
  feed_url: string;
  title: string;
  author: string | null;
  description: string | null;
  image_url: string | null;
  language: string | null;
  categories: string[];
  episode_count: number | null;
};

// Preview-only episode shape (returned by PodcastIndex; no DB ids/flags).
export type PodcastPreviewEpisode = {
  podcastindex_episode_id: number;
  guid: string | null;
  title: string;
  description: string | null;
  audio_url: string;
  audio_mime: string | null;
  duration_seconds: number | null;
  image_url: string | null;
  published_at: string | null;
  episode_number: number | null;
  season_number: number | null;
};

export const fetchCategories = async (): Promise<PodcastCategory[]> => {
  // Source of truth = edge function (it reads cache + refreshes from
  // PodcastIndex when stale). The cache table is server-managed; the client
  // does not read it directly.
  const { data, error } = await supabase.functions.invoke("podcasts-categories", { body: {} });
  if (error) throw new Error(error.message);
  const cats = (data as { categories?: PodcastCategory[] } | null)?.categories ?? [];
  return cats;
};

export const previewEpisodesByFeedId = async (
  feedId: number,
  max = 20,
): Promise<{ episodes: PodcastPreviewEpisode[]; feed: PodcastSearchResult | null }> => {
  const { data, error } = await supabase.functions.invoke("podcasts-preview-episodes", {
    body: { feed_id: feedId, max },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return {
    episodes: (data?.episodes ?? []) as PodcastPreviewEpisode[],
    feed: (data?.feed ?? null) as PodcastSearchResult | null,
  };
};

export const searchPodcasts = async (args: {
  term?: string;
  cat?: string;
  lang?: string;
  max?: number;
}): Promise<PodcastSearchResult[]> => {
  const { data, error } = await supabase.functions.invoke("podcasts-search", { body: args });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return (data?.results ?? []) as PodcastSearchResult[];
};

// ---------------------------------------------------------------------
// Subscriptions
// ---------------------------------------------------------------------

export const fetchSubscriptions = async (workspaceIds: string[]): Promise<PodcastSubscription[]> => {
  if (!workspaceIds.length) return [];
  const { data, error } = await supabase
    .from("podcast_subscriptions")
    .select("*")
    .in("workspace_id", workspaceIds)
    .order("title", { ascending: true });
  if (error) throw new Error(error.message);
  return data ?? [];
};

export const subscribeByFeedId = async (
  workspaceId: string,
  feedId: number,
): Promise<{ subscription: PodcastSubscription; imported: number }> => {
  const { data, error } = await supabase.functions.invoke("podcasts-subscribe", {
    body: { workspace_id: workspaceId, feed_id: feedId },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
};

export const subscribeByFeedUrl = async (
  workspaceId: string,
  feedUrl: string,
): Promise<{ subscription: PodcastSubscription; imported: number }> => {
  const { data, error } = await supabase.functions.invoke("podcasts-subscribe", {
    body: { workspace_id: workspaceId, feed_url: feedUrl },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
};

export const unsubscribe = async (subscriptionId: string): Promise<void> => {
  const { error } = await supabase
    .from("podcast_subscriptions")
    .delete()
    .eq("id", subscriptionId);
  if (error) throw new Error(error.message);
};

export const syncSubscription = async (subscriptionId: string) => {
  const { data, error } = await supabase.functions.invoke("podcasts-subscribe", {
    body: { resync_subscription_id: subscriptionId },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data as { ok: true; new_items: number; latest_episode_published_at: string | null };
};

// ---- "New episode" badge -----------------------------------------------
// "New" = latest_episode_published_at on the subscription is newer than
// last_seen_at. Both columns live on podcast_subscriptions. last_seen_at
// is stamped to now() on subscribe AND whenever the user opens the sub.

export const hasNewEpisode = (sub: PodcastSubscription): boolean => {
  const latest = sub.latest_episode_published_at;
  if (!latest) return false;
  const seen = sub.last_seen_at;
  if (!seen) return true;
  return Date.parse(latest) > Date.parse(seen);
};

export const markSubscriptionSeen = async (subscriptionId: string): Promise<void> => {
  const { error } = await supabase
    .from("podcast_subscriptions")
    .update({ last_seen_at: new Date().toISOString() })
    .eq("id", subscriptionId);
  if (error) throw new Error(error.message);
};

// Refresh `latest_episode_published_at` for a subscription using the
// already-fetched live-feed episode list. Cheap: no extra PI call when
// the caller has just loaded the preview anyway.
export const refreshLatestPublishedFromPreview = async (
  subscriptionId: string,
  episodes: PodcastPreviewEpisode[],
): Promise<string | null> => {
  const latest = episodes
    .map((e) => e.published_at)
    .filter((t): t is string => !!t)
    .sort()
    .at(-1) ?? null;
  if (!latest) return null;
  await supabase
    .from("podcast_subscriptions")
    .update({ latest_episode_published_at: latest })
    .eq("id", subscriptionId);
  return latest;
};

// Page-load check: for each subscription, fetch the single most recent
// episode from PodcastIndex (cached server-side for 5 min) and update
// latest_episode_published_at if it changed. Returns the new
// PodcastSubscription rows so callers can re-render with fresh badges.
export const refreshAllLatestPublished = async (
  subs: PodcastSubscription[],
  concurrency = 5,
): Promise<PodcastSubscription[]> => {
  if (subs.length === 0) return subs;
  const updated = new Map<string, string | null>();
  let i = 0;
  const workers = Array.from({ length: Math.min(concurrency, subs.length) }, async () => {
    while (i < subs.length) {
      const sub = subs[i++];
      try {
        const { episodes } = await previewEpisodesByFeedId(sub.podcastindex_feed_id, 1);
        const latest = episodes[0]?.published_at ?? null;
        if (latest && latest !== sub.latest_episode_published_at) {
          await supabase
            .from("podcast_subscriptions")
            .update({ latest_episode_published_at: latest })
            .eq("id", sub.id);
          updated.set(sub.id, latest);
        }
      } catch {
        // best-effort; skip on error
      }
    }
  });
  await Promise.all(workers);
  if (updated.size === 0) return subs;
  return subs.map((s) =>
    updated.has(s.id) ? { ...s, latest_episode_published_at: updated.get(s.id)! } : s,
  );
};

// ---------------------------------------------------------------------
// Episodes — reads, mutations, lazy DB-row materialization
// ---------------------------------------------------------------------

export const fetchEpisodes = async (args: {
  workspaceIds: string[];
  filter: EpisodeFilter;
  subscriptionId?: string | null;
}): Promise<PodcastEpisode[]> => {
  const { workspaceIds, filter, subscriptionId } = args;
  if (!workspaceIds.length) return [];
  let q = supabase
    .from("podcast_episodes")
    .select("*")
    .in("workspace_id", workspaceIds)
    .order("published_at", { ascending: false, nullsFirst: false })
    .limit(300);
  if (subscriptionId) q = q.eq("subscription_id", subscriptionId);
  if (filter === "starred") q = q.eq("is_starred", true);
  else if (filter === "archive") q = q.eq("is_archived", true);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return data ?? [];
};

export const updateEpisode = async (
  id: string,
  patch: Partial<Pick<PodcastEpisode, "is_read" | "is_archived" | "is_starred">>,
) => {
  const { error } = await supabase.from("podcast_episodes").update(patch).eq("id", id);
  if (error) throw new Error(error.message);
};

// ---- Lazy episode-row materialization ----------------------------------
// In Subscribed mode the episode LIST comes from the live PodcastIndex
// feed (same as Discover preview), but per-episode state (is_read,
// is_starred, is_archived) and transcripts live on `podcast_episodes`.
// When the user takes a stateful action on a feed episode that has no DB
// row yet, mint it on demand.

export type EnsureEpisodeInput = {
  podcastindex_episode_id: number;
  guid: string | null;
  title: string;
  description: string | null;
  audio_url: string;
  audio_mime: string | null;
  duration_seconds: number | null;
  image_url: string | null;
  published_at: string | null;
  episode_number: number | null;
  season_number: number | null;
};

export const ensureEpisodeRow = async (
  sub: PodcastSubscription,
  ep: EnsureEpisodeInput,
): Promise<PodcastEpisode> => {
  // Try by (subscription_id, podcastindex_episode_id) first.
  if (ep.podcastindex_episode_id) {
    const { data: existing } = await supabase
      .from("podcast_episodes")
      .select("*")
      .eq("subscription_id", sub.id)
      .eq("podcastindex_episode_id", ep.podcastindex_episode_id)
      .maybeSingle();
    if (existing) return existing;
  }
  // Fallback: by guid (older feeds may rotate ids).
  if (ep.guid) {
    const { data: existing } = await supabase
      .from("podcast_episodes")
      .select("*")
      .eq("subscription_id", sub.id)
      .eq("guid", ep.guid)
      .maybeSingle();
    if (existing) return existing;
  }
  // Insert
  const { data, error } = await supabase
    .from("podcast_episodes")
    .insert({
      subscription_id: sub.id,
      workspace_id: sub.workspace_id,
      podcastindex_episode_id: ep.podcastindex_episode_id,
      guid: ep.guid,
      title: ep.title,
      description: ep.description,
      audio_url: ep.audio_url,
      audio_mime: ep.audio_mime,
      duration_seconds: ep.duration_seconds,
      image_url: ep.image_url,
      published_at: ep.published_at,
      episode_number: ep.episode_number,
      season_number: ep.season_number,
    })
    .select("*")
    .single();
  if (error) throw new Error(error.message);
  return data;
};

// Standalone (no subscription) variant — used when adding a Discover
// episode to My Library or saving a native transcript without
// subscribing to the whole show.
export const ensureEpisodeRowStandalone = async (
  workspaceId: string,
  ep: EnsureEpisodeInput,
): Promise<PodcastEpisode> => {
  if (ep.podcastindex_episode_id) {
    const { data: existing } = await supabase
      .from("podcast_episodes")
      .select("*")
      .eq("workspace_id", workspaceId)
      .is("subscription_id", null)
      .eq("podcastindex_episode_id", ep.podcastindex_episode_id)
      .maybeSingle();
    if (existing) return existing;
    // Also reuse an already-subscribed row if it exists for this PI id.
    const { data: subbed } = await supabase
      .from("podcast_episodes")
      .select("*")
      .eq("workspace_id", workspaceId)
      .eq("podcastindex_episode_id", ep.podcastindex_episode_id)
      .maybeSingle();
    if (subbed) return subbed;
  }
  const { data, error } = await supabase
    .from("podcast_episodes")
    .insert({
      subscription_id: null,
      workspace_id: workspaceId,
      podcastindex_episode_id: ep.podcastindex_episode_id,
      guid: ep.guid,
      title: ep.title,
      description: ep.description,
      audio_url: ep.audio_url,
      audio_mime: ep.audio_mime,
      duration_seconds: ep.duration_seconds,
      image_url: ep.image_url,
      published_at: ep.published_at,
      episode_number: ep.episode_number,
      season_number: ep.season_number,
    })
    .select("*")
    .single();
  if (error) throw new Error(error.message);
  return data;
};

// Fetch overlay rows (is_read/is_starred/is_archived/transcript pointers)
// for an entire subscription. Keyed off podcastindex_episode_id + guid so
// callers can map feed episodes → DB state without a join.
export const fetchEpisodeOverlayForSub = async (
  subscriptionId: string,
): Promise<PodcastEpisode[]> => {
  const { data, error } = await supabase
    .from("podcast_episodes")
    .select("*")
    .eq("subscription_id", subscriptionId)
    .limit(2000);
  if (error) throw new Error(error.message);
  return data ?? [];
};

// ---------------------------------------------------------------------
// Transcripts (saved Deepgram + on-demand native)
// ---------------------------------------------------------------------

export const fetchTranscript = async (episodeId: string): Promise<PodcastTranscript | null> => {
  const { data, error } = await supabase
    .from("podcast_episode_transcripts")
    .select("*")
    .eq("episode_id", episodeId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data;
};

export const transcribeEpisode = async (
  episodeId: string,
): Promise<{ transcript: PodcastTranscript; cost_credits: number }> => {
  const { data, error } = await supabase.functions.invoke("podcasts-transcribe", {
    body: { episode_id: episodeId },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
};

// ---- Native transcripts ------------------------------------------------
// Loaded on-demand from the publisher's <podcast:transcript> URL. Never
// written to the DB unless the user explicitly saves (or adds the episode
// to My Library, which saves as a side-effect for stable citation).

export type NativeTranscriptResult = {
  available: boolean;
  reason?: string;
  source_url?: string;
  source_type?: string | null;
  transcript?: {
    text: string;
    words: Array<{ word: string; start: number; end: number }> | null;
    paragraphs: unknown | null;
    language: string | null;
  };
  duration_seconds?: number | null;
};

export const fetchNativeTranscript = async (
  podcastindexEpisodeId: number,
): Promise<NativeTranscriptResult> => {
  const { data, error } = await supabase.functions.invoke("podcasts-native-transcript", {
    body: { podcastindex_episode_id: podcastindexEpisodeId },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data as NativeTranscriptResult;
};

export const saveNativeTranscript = async (
  episodeId: string,
  workspaceId: string,
  native: NonNullable<NativeTranscriptResult["transcript"]>,
  durationSeconds: number | null,
): Promise<PodcastTranscript> => {
  // Upsert: if a row already exists for this episode, keep it.
  const { data: existing } = await supabase
    .from("podcast_episode_transcripts")
    .select("*")
    .eq("episode_id", episodeId)
    .maybeSingle();
  if (existing) return existing;
  const { data, error } = await supabase
    .from("podcast_episode_transcripts")
    .insert({
      episode_id: episodeId,
      workspace_id: workspaceId,
      text: native.text,
      paragraphs: native.paragraphs as never,
      words: native.words as never,
      language: native.language,
      model: "native",
      duration_seconds: durationSeconds,
      cost_credits: 0,
    })
    .select("*")
    .single();
  if (error) throw new Error(error.message);
  return data;
};

// ---------------------------------------------------------------------
// Promote to My Library (episode-level)
// ---------------------------------------------------------------------


// Episode-level promotion — preferred for citations.
export type EpisodePromoteShow = {
  title: string;
  author: string | null;
  feed_url: string;
  image_url: string | null;
  language: string | null;
  podcastindex_feed_id: number | null;
};

/**
 * The `library_items.source_id` value an episode would (or does) occupy when
 * promoted via `promoteEpisodeToLibrary`. Exported so UI can check "already in
 * My Library?" using the same key the writer uses.
 */
export const episodeLibrarySourceId = (
  episode: Pick<PodcastEpisode, "podcastindex_episode_id" | "guid" | "id">,
): string =>
  episode.podcastindex_episode_id
    ? String(episode.podcastindex_episode_id)
    : (episode.guid ?? episode.id);

export const promoteEpisodeToLibrary = async (
  workspaceId: string,
  episode: PodcastEpisode,
  show: EpisodePromoteShow,
): Promise<{ library_item_id: string; created: boolean }> => {
  const sourceId = episodeLibrarySourceId(episode);
  const { data: existing, error: lookupErr } = await supabase
    .from("library_items")
    .select("id")
    .eq("workspace_id", workspaceId)
    .eq("source_provider", "podcastindex")
    .eq("source_id", sourceId)
    .maybeSingle();
  if (lookupErr) throw new Error(lookupErr.message);
  if (existing) return { library_item_id: existing.id, created: false };

  const today = new Date().toISOString().slice(0, 10);
  const { data, error } = await supabase
    .from("library_items")
    .insert({
      workspace_id: workspaceId,
      source_type: "podcast",
      source_provider: "podcastindex",
      source_id: sourceId,
      title: episode.title,
      container_title: show.title,
      author_text: show.author,
      authors: show.author ? [{ name: show.author }] : [],
      language_code: show.language,
      url: episode.audio_url ?? show.feed_url,
      cover_url: episode.image_url ?? show.image_url,
      published_date: episode.published_at ? episode.published_at.slice(0, 10) : null,
      accessed_date: today,
      status: "to_read",
      metadata: {
        podcast_feed_id: show.podcastindex_feed_id,
        episode_guid: episode.guid,
        podcastindex_episode_id: episode.podcastindex_episode_id,
        duration_seconds: episode.duration_seconds,
      } as never,
    })
    .select("id")
    .single();
  if (error) throw new Error(error.message);
  return { library_item_id: data.id, created: true };
};
